import { tenants } from "./tenants.js";
import { FeatureFlags as FeatureFlagsSchema, PmBoardConfig as PmBoardConfigSchema } from "./schema.js";
/**
 * Browser-safe, environment-INDEPENDENT feature-flag resolution by realm (FN / ADR-014). Feature flags live on
 * `TenantConfig` (not per-environment), so the client only needs the verified `session.realm` — no host/env, so this
 * does NOT use `createTenantRegistry` (which requires an `EnvName` the browser doesn't have).
 *
 * Fails SAFE: an unknown realm (or a realm not in the config-as-code registry) gets the schema defaults, so a
 * misconfiguration can never, e.g., silently flip a tenant into an unfinished UI variant.
 */
const DEFAULT_FLAGS = FeatureFlagsSchema.parse({
// Everything omitted → the schema's own defaults, which are the fail-safe ones:
// `facilityNav` → "classic", `ratingsEnabled` → true, `qrCodeWithLink` → true.
});
const DEFAULT_PM_BOARD = PmBoardConfigSchema.parse({});
export function resolveFeatureFlags(realm) {
    return tenants.find((t) => t.realm === realm)?.featureFlags ?? DEFAULT_FLAGS;
}
/** The facility-navigation variant for a realm — `"classic"` for any unknown/unflagged tenant (FN). */
export function facilityNavFor(realm) {
    return resolveFeatureFlags(realm).facilityNav;
}
/** Are ratings enabled for a realm (ADR-034 D6)? Unknown realm ⇒ the schema default (on). */
export function ratingsEnabledFor(realm) {
    return resolveFeatureFlags(realm).ratingsEnabled;
}
/** The PM board's seed values for a realm (ADR-055). Unknown realm ⇒ the schema defaults: closed to ordinary users. */
export function pmBoardConfigFor(realm) {
    return tenants.find((t) => t.realm === realm)?.pmBoard ?? DEFAULT_PM_BOARD;
}
/**
 * The QR-label settings for a realm (ADR-029): whether a single-facility code prints as a deep link, and the label's
 * heading — the LITERAL "XR Creator" when the tenant never set one, as the legacy printed. Unknown realm ⇒ the
 * legacy defaults, so the view never has to special-case a missing tenant.
 */
export function qrConfigFor(realm) {
    const t = tenants.find((x) => x.realm === realm);
    return { withLink: t?.featureFlags.qrCodeWithLink ?? true, labelTitle: t?.featureFlags.qrCodeLabelTitle ?? "XR Creator" };
}
/**
 * The origin a printed QR deep link must carry (ADR-029, diff-panel Medium). `window.location.origin` is right on the
 * tenant's canonical host but WRONG on a path-prefixed alias: `xrarea.arcreator.at/lumpi` would print
 * `xrarea.arcreator.at?arc_facility=…`, which the path-less host resolves to xr-area — a label opening the wrong
 * realm. So: when the page's hostname is one of the realm's ALIAS hosts, print the canonical host of that environment
 * (same protocol + port); otherwise the page's own origin (canonical hosts and local dev, where the port matters).
 */
export function qrDeepLinkBase(realm, loc) {
    const t = tenants.find((x) => x.realm === realm);
    const host = loc.hostname.toLowerCase();
    const env = t ? Object.values(t.environments).find((e) => e.aliases.some((a) => a.host.toLowerCase() === host)) : undefined;
    if (!env || env.canonicalHost.toLowerCase() === host)
        return loc.origin;
    return `${loc.protocol}//${env.canonicalHost}${loc.port ? `:${loc.port}` : ""}`;
}
//# sourceMappingURL=feature-flags.js.map